import { ApiResponse } from '@/models/api-response';
import { callApi } from './api';
import { MediaUploadResponse, MediaGetResponse, MediaUploadOptions, MediaUploadCreateResponse } from '@/models/media';
import { useQuery } from '@tanstack/react-query';
import * as UpChunk from '@mux/upchunk';

class MediaService {
    private static instance: MediaService;
    private baseUrl: string;

    private constructor() {
        this.baseUrl = import.meta.env.VITE_API_URL_V1 + '/media';
    }

    public static getInstance(): MediaService {
        if (!MediaService.instance) {
            MediaService.instance = new MediaService();
        }
        return MediaService.instance;
    }

    public async uploadFile(file: File, options: MediaUploadOptions): Promise<ApiResponse<MediaUploadResponse>> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', options.name);

        if (options.description) {
            formData.append('description', options.description);
        }

        return callApi({
            url: `${this.baseUrl}/upload`,
            method: 'POST',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    public async getMedia(mediaId: string) {
        return await callApi<MediaGetResponse>({
            url: `${this.baseUrl}/${mediaId}`,
            method: 'GET',
        });
    }

    public async deleteMedia(mediaId: string): Promise<ApiResponse<void>> {
        return callApi({
            url: `${this.baseUrl}/${mediaId}`,
            method: 'DELETE',
        });
    }

    public async uploadFileChunked(
        file: File,
        options: MediaUploadOptions,
        onProgress?: (percentage: number) => void,
    ): Promise<MediaUploadResponse | undefined> {
        try {
            if (file.size > 500 * 1024 * 1024) {
                throw new Error('File size exceeds maximum limit of 500MB');
            }

            const createResponse = await callApi<MediaUploadCreateResponse>({
                url: `${this.baseUrl}/upload-chunked`,
                method: 'POST',
                data: {
                    name: options.name,
                    description: options.description,
                    size: file.size,
                    mimeType: file.type,
                },
            });
            const uploadData = createResponse.data as MediaUploadCreateResponse;

            return new Promise((resolve, reject) => {
                const upload = UpChunk.createUpload({
                    endpoint: uploadData.url,
                    file: file,
                    chunkSize: 30720, //  ~30 MB chunks
                });
                upload.on('error', (error) => reject(error));
                upload.on('progress', (progress) => {
                    if (onProgress && progress.detail < 96) {
                        onProgress(progress.detail.toFixed(2));
                    }
                });
                upload.on('success', async () => {
                    try {
                        // After successful upload, we need to poll for the asset_id
                        onProgress?.(96); // Show that we're processing
                        let assetId = uploadData.asset_id;
                        if (!assetId) {
                            // Mux assigns the asset id asynchronously, a moment AFTER the
                            // chunked upload finishes — and the API's upload-status poll can
                            // time out (or the long request can time out client-side) before
                            // the asset exists, returning no assetId. Retry until it appears
                            // rather than resolving with an empty id (which would leave the
                            // lesson with no media handle, failing the "needs media" check
                            // at save while the preview misleadingly shows an empty player).
                            const maxAttempts = 8;
                            for (let attempt = 0; attempt < maxAttempts && !assetId; attempt++) {
                                if (attempt > 0) await new Promise((r) => setTimeout(r, 2000));
                                const assetResponse = await callApi<{ assetId: string }>({
                                    url: `${this.baseUrl}/upload-status/${uploadData.id}`,
                                    method: 'GET',
                                });
                                assetId = assetResponse.data?.assetId as string;
                            }
                        }
                        if (!assetId) {
                            // Don't resolve with an empty id — surface the failure so the
                            // caller doesn't store an unusable media reference.
                            reject(new Error('Upload finished but Mux did not return an asset id in time. Please try uploading again.'));
                            return;
                        }
                        onProgress?.(100);
                        resolve({ uri: assetId });
                    } catch (error) {
                        reject(error);
                    }
                });
            });
        } catch (error) {
            console.error('uploadFileChunked Error: ' + error);
        }
    }
}

export default MediaService;
export const mediaService = MediaService.getInstance();

export const useMedia = (mediaId: string) => {
    return useQuery({
        queryKey: ['media', mediaId],
        queryFn: async () => await mediaService.getMedia(mediaId),
        enabled: !!mediaId,
    });
};
